import React, { useState, useEffect, useCallback } from "react";
import { useQuery, gql } from "@apollo/client";
import Sidebar from "../Sidebar";
import Layout from "../Layout";
import Loader from "../../Loader";
import FilterBar from "./FilterBar";
import TimeSeriesBookingsChart from "./TimeSeriesBookingChart";
import dayjs from "dayjs";
import PaymentsSummaryCard from "./EarningsCard";
import BookingsSummaryCard from "./BookingsCard";
import UserRetentionSummaryCard from "./UserRetentionSummaryCard";
import StatusBreakdownChart from "./StatusBreakdownChart";
import TimeSeriesEarningsChart from "./TimeSeriesEarningsChart";
import VerificationStatusBreakdownChart from "./VerificationStatusBreakdownChart";
import FilteredBookings from "../AdminBookings/FilteredBookings";
import DeclineBreakdownCards from "./DeclineBreakdownCards";
import PincodeBubbleChart from "./PincodeBubbleChart";
import EmployeeFeedbackSummaryCard from "./EmployeeFeedbackSummaryCard";
import EmployeeStatsPieCharts from "./EmployeeStatsPieCharts";
import BookingSourceCard from "./BookingSourceCard";
import SupportFeedbackSummaryCard from "./SupportFeedbackSummaryCard";
import EmployeeTimelineDashboard from "../AdminBookings/EmployeeTimelineDashboard";
import BookingFunnelWidget from "./BookingFunnelWidget";
import InventoryScanAnalyticsWidget from "./InventoryScanAnalyticsWidget";
import EmployeeAvailabilityWidget from "./EmployeeAvailabilityWidget";
import PaymentFlowAnalyticsWidget from "./PaymentFlowAnalyticsWidget";

const GET_EMPLOYEES = gql`
  query getEmployeeList($data: EmployeeFilterInput) {
    getEmployeelist(data: $data) {
      employee {
        id
        fullName
      }
    }
  }
`;

const GET_TIME_SERIES = gql`
  query getTimeSeriesStats($data: BookingStatsFilter!) {
    getTimeSeriesStats(data: $data) {
      status
      message
      employeeStats {
        employeeId
        fullName
        earnings
        bookingsCount
      }
      data {
        label
        total
        statuses
        earnings
        verificationStatuses
        unverifiedEarnings
        verifiedCount
        unverifiedCount
        completedUnverifiedCount
        retainedUsers
        newUsers
        employeeFeedbackSubmitted
        employeeFeedbackNotSubmitted
        feedbackCallsPending
        feedbackCallsCompleted
        feedbackIssuesPending
        feedbackIssuesResolved
        cancellationCallsPending
        cancellationCallsCompleted
        bookingTypes
        declineCategories
        declinedByEmployee
      }
    }
  }
`;
const GET_PINCODE_BUBBLE = gql`
  query getPincodeBubbleStats($data: PincodeBubbleInput!) {
    getPincodeBubbleStats(data: $data) {
      status
      data {
        pincode
        bookingcount
      }
    }
  }
`;

const AdminDashboard = () => {
  const [bookingFilters, setBookingFilters] = useState(null);
  const [showDeclineBreakdown, setShowDeclineBreakdown] = useState(false);

  const [filters, setFilters] = useState(() => {
    const today = dayjs().format("YYYY-MM-DD");
    const sevenDaysAgo = dayjs().subtract(6, "day").format("YYYY-MM-DD");

    return {
      startDate: sevenDaysAgo,
      endDate: today,
      frequency: "daily",
      employeeId: "",
      employeeIds: [], // for multi-select (if needed)
    };
  });

  const { data: heatmapData, refetch: refetchHeatmap, loading: heatmapLoading } = useQuery(GET_PINCODE_BUBBLE, {
    variables: { data: filters },
    notifyOnNetworkStatusChange: true,
  });

  const { data: empData } = useQuery(GET_EMPLOYEES, {
    variables: { data: { bookableOrEmployed: true } },
  });

  const employees = empData?.getEmployeelist?.employee || [];

  const { data, loading, refetch } = useQuery(GET_TIME_SERIES, {
    variables: { data: filters },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  /* -----------------------------
     Click Handlers (Open Bookings)
  ------------------------------*/

  const handleStatusClick = (status) => {
    if (status === "Declined") {
      setShowDeclineBreakdown(true);
      return;
    }
    setBookingFilters({
      status: [status],
      queryStartDate: filters.startDate,
      queryEndDate: filters.endDate,
      verificationStatus: null,
      employeeId: filters.employeeId || null,
      employeeIds: filters.employeeIds || [], // include multi-select employee IDs if needed
    });
  };

  const handleDeclineCategoryClick = (declineCategory) => {
    setBookingFilters({
      status: ["Declined"],
      declineCategory,
      queryStartDate: filters.startDate,
      queryEndDate: filters.endDate,
      verificationStatus: null,
      employeeId: filters.employeeId || null,
      employeeIds: filters.employeeIds || [],
    });
  };

  // Declines the professional made themselves from the employee app
  // (Decline -> customer code), whatever fault category they map to.
  const handleDeclinedByEmployeeClick = () => {
    setBookingFilters({
      status: ["Declined"],
      cancelledByType: "EMPLOYEE",
      queryStartDate: filters.startDate,
      queryEndDate: filters.endDate,
      verificationStatus: null,
      employeeId: filters.employeeId || null,
      employeeIds: filters.employeeIds || [],
    });
  };

  const handleViewAllDeclined = () => {
    setBookingFilters({
      status: ["Declined"],
      queryStartDate: filters.startDate,
      queryEndDate: filters.endDate,
      verificationStatus: null,
      employeeId: filters.employeeId || null,
      employeeIds: filters.employeeIds || [],
    });
  };

  const handleVerificationClick = (verificationStatus) => {
    console.log("Verification status clicked:", verificationStatus);
    console.log("Current filters:", filters);
    setBookingFilters({
      status: [],
      queryStartDate: filters.startDate,
      queryEndDate: filters.endDate,
      verificationStatus:
        verificationStatus == "VERIFIED"
          ? "PAYMENT_VERIFIED"
          : verificationStatus, // Map VERIFIED to PAYMENT_VERIFIED for filtering
      employeeId: filters.employeeId || null,
      employeeIds: filters.employeeIds || [], // include multi-select employee IDs if needed
    });
  };
  const handleCompletedUnverifiedClick = () => {
    setBookingFilters({
      status: ["Completed"],
      queryStartDate: filters.startDate,
      queryEndDate: filters.endDate,
      verificationStatus: "NOT_VERIFIED",
      employeeId: filters.employeeId || null,
      employeeIds: filters.employeeIds || [],
    });
  };

  const handleCustomerTypeClick = (customerType) => {
    setBookingFilters({
      status: [],
      queryStartDate: filters.startDate,
      queryEndDate: filters.endDate,
      verificationStatus: null,
      employeeId: filters.employeeId || null,
      employeeIds: filters.employeeIds || [], // include multi-select employee IDs if needed
      customerType, // 🔥 NEW
    });
  };
  const handleFeedbackClick = (type) => {
    setBookingFilters({
      status: [],
      queryStartDate: filters.startDate,
      queryEndDate: filters.endDate,
      verificationStatus: null,
      employeeId: filters.employeeId || null,
      employeeIds: filters.employeeIds || [],
      isAdminToEmpFeedbackCompleted: type === "SUBMITTED" ? true : false, // 🔥 backend filter
    });
  };

  const handleFunnelDropClick = (dropStage) => {
    setBookingFilters({
      status: [],
      queryStartDate: filters.startDate,
      queryEndDate: filters.endDate,
      verificationStatus: null,
      employeeId: filters.employeeId || null,
      employeeIds: filters.employeeIds || [],
      funnelDropStage: dropStage,
    });
  };

  const handleBackToDashboard = () => {
    setBookingFilters(null);
  };

  const handleCloseDeclineBreakdown = () => {
    setShowDeclineBreakdown(false);
  };

  // if (loading && !bookingFilters) return <Loader />;

  const rows = data?.getTimeSeriesStats?.data || [];

  const totalBookings = rows.reduce((acc, r) => acc + (r.total || 0), 0);
  const totalVerified = rows.reduce(
    (acc, r) => acc + (r.verifiedCount || 0),
    0,
  );
  const totalUnverifiedBookings = rows.reduce(
    (acc, r) => acc + (r.unverifiedCount || 0),
    0,
  );
  const totalCompletedUnverified = rows.reduce(
    (acc, r) => acc + (r.completedUnverifiedCount || 0),
    0,
  );
  const totalEarning = rows.reduce((acc, r) => acc + (r.earnings || 0), 0);
  const totalUnverifiedEarnings = rows.reduce(
    (acc, r) => acc + (r.unverifiedEarnings || 0),
    0,
  );
  const totalNewUsers = rows.reduce((acc, r) => acc + (r.newUsers || 0), 0);
  const totalRetainedUsers = rows.reduce(
    (acc, r) => acc + (r.retainedUsers || 0),
    0,
  );

  const statusCounts = rows.reduce((acc, row) => {
    Object.entries(row.statuses || {}).forEach(([status, count]) => {
      acc[status] = (acc[status] || 0) + count;
    });
    return acc;
  }, {});

  const declineCategoryCounts = rows.reduce((acc, row) => {
    Object.entries(row.declineCategories || {}).forEach(([category, count]) => {
      acc[category] = (acc[category] || 0) + count;
    });
    return acc;
  }, {});

  const declinedByEmployeeCount = rows.reduce(
    (sum, row) => sum + (row.declinedByEmployee || 0),
    0,
  );

  const verificationStatusCounts = rows.reduce((acc, row) => {
    Object.entries(row.verificationStatuses || {}).forEach(
      ([status, count]) => {
        acc[status] = (acc[status] || 0) + count;
      },
    );
    return acc;
  }, {});

  const totalFeedbackSubmitted = rows.reduce(
    (acc, r) => acc + (r.employeeFeedbackSubmitted || 0),
    0,
  );

  const totalFeedbackNotSubmitted = rows.reduce(
    (acc, r) => acc + (r.employeeFeedbackNotSubmitted || 0),
    0,
  );

  const bookingTypeCounts = rows.reduce((acc, row) => {
    console.log("Processing row for booking types:", row.bookingTypes);
    Object.entries(row.bookingTypes || {}).forEach(([type, counts]) => {
      if (!acc[type]) {
        acc[type] = { total: 0, verified: 0 };
      }
      acc[type].total += counts.total || 0;
      acc[type].verified += counts.verified || 0;
    });
    return acc;
  }, {});

  const totalFeedbackCallsPending = rows.reduce((acc, r) => acc + (r.feedbackCallsPending || 0), 0);
  const totalFeedbackCallsCompleted = rows.reduce((acc, r) => acc + (r.feedbackCallsCompleted || 0), 0);
  const totalFeedbackIssuesPending = rows.reduce((acc, r) => acc + (r.feedbackIssuesPending || 0), 0);
  const totalFeedbackIssuesResolved = rows.reduce((acc, r) => acc + (r.feedbackIssuesResolved || 0), 0);
  const totalCancellationCallsPending = rows.reduce((acc, r) => acc + (r.cancellationCallsPending || 0), 0);
  const totalCancellationCallsCompleted = rows.reduce((acc, r) => acc + (r.cancellationCallsCompleted || 0), 0);

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="relative flex flex-col space-y-6 p-6">
          {/* DASHBOARD CONTENT (always mounted) */}
          <FilterBar
            employees={employees}
            onFilterChange={handleFilterChange}
            showFrequencyFilter={true}
            onRefresh={() => {
              refetchHeatmap();
              refetch();
            }}
            loading={loading || heatmapLoading}
          />

          {loading && !bookingFilters ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader />
            </div>
          ) : (
            <>
              <div className="flex flex-col xl:flex-row items-center xl:items-stretch gap-6">
                <PaymentsSummaryCard
                  totalAmount={totalEarning + totalUnverifiedEarnings}
                  verifiedAmount={totalEarning}
                  verifiedCount={totalVerified}
                  unverifiedAmount={totalUnverifiedEarnings}
                  unverifiedCount={totalUnverifiedBookings}
                />

                <BookingsSummaryCard
                  totalBookings={totalBookings}
                  statusCounts={statusCounts}
                  verificationStatusCounts={verificationStatusCounts}
                  completedUnverifiedCount={totalCompletedUnverified}
                  onStatusClick={handleStatusClick}
                  onVerificationStatusClick={handleVerificationClick}
                  onCompletedUnverifiedClick={handleCompletedUnverifiedClick}
                />

                <UserRetentionSummaryCard
                  totalBookings={totalBookings}
                  newUsers={totalNewUsers}
                  retainedUsers={totalRetainedUsers}
                  onCustomerTypeClick={handleCustomerTypeClick}
                />
              </div>
              <div className="flex flex-col xl:flex-row items-center xl:items-stretch gap-6">
                <BookingSourceCard bookingTypeCounts={bookingTypeCounts} />
                <EmployeeFeedbackSummaryCard
                  submitted={totalFeedbackSubmitted}
                  notSubmitted={totalFeedbackNotSubmitted}
                  onFeedbackClick={handleFeedbackClick}
                />
                <SupportFeedbackSummaryCard
                  feedbackCallsPending={totalFeedbackCallsPending}
                  feedbackCallsCompleted={totalFeedbackCallsCompleted}
                  feedbackIssuesPending={totalFeedbackIssuesPending}
                  feedbackIssuesResolved={totalFeedbackIssuesResolved}
                  cancellationCallsPending={totalCancellationCallsPending}
                  cancellationCallsCompleted={totalCancellationCallsCompleted}
                />
              </div>

              <h3 className="text-[#FBB033] font-bold text-xl">
                Total Bookings
              </h3>
              <TimeSeriesBookingsChart rows={rows} />

              <h3 className="text-[#FBB033] font-bold text-xl">
                Status Breakdown
              </h3>
              <StatusBreakdownChart rows={rows} />

              <h3 className="text-[#FBB033] font-bold text-xl">
                Verification Status Breakdown
              </h3>
              <VerificationStatusBreakdownChart rows={rows} />

              <h3 className="text-[#FBB033] font-bold text-xl">
                Total Earnings
              </h3>
              <TimeSeriesEarningsChart rows={rows} />

              <h3 className="text-[#FBB033] font-bold text-xl mt-6">
                Employee Performance Overview
              </h3>
              <EmployeeStatsPieCharts
                employeeStats={data?.getTimeSeriesStats?.employeeStats || []}
              />

              <div className="mt-6">
                <BookingFunnelWidget filters={filters} onDropClick={handleFunnelDropClick} />
              </div>

              <div className="mt-6">
                <EmployeeAvailabilityWidget filters={filters} />
              </div>

              <div className="mt-6">
                <InventoryScanAnalyticsWidget filters={filters} />
              </div>

              <div className="mt-6">
                <PaymentFlowAnalyticsWidget filters={filters} />
              </div>

              <h3 className="text-[#FBB033] font-bold text-xl">
                Top Pincode Distribution
              </h3>
              <PincodeBubbleChart
                data={heatmapData?.getPincodeBubbleStats?.data || []}
              />

              <div className="mt-8 bg-black/40 rounded-xl overflow-hidden border border-gray-800">
                <EmployeeTimelineDashboard
                  employees={employees}
                  employeeIds={filters.employeeIds || []}
                  employeeId={filters.employeeId || undefined}
                />
              </div>
            </>
          )}

          {/* SLIDE PANEL (Declined Breakdown Overlay) */}
          {showDeclineBreakdown && !bookingFilters && (
            <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-end">
              <div className="w-[85%] xl:w-[70%] h-full bg-[#0f0f0f] shadow-2xl transform transition-transform duration-300 translate-x-0 overflow-auto">
                <DeclineBreakdownCards
                  declineCategoryCounts={declineCategoryCounts}
                  declinedByEmployeeCount={declinedByEmployeeCount}
                  onDeclinedByEmployeeClick={handleDeclinedByEmployeeClick}
                  onCategoryClick={handleDeclineCategoryClick}
                  onViewAll={handleViewAllDeclined}
                  onBack={handleCloseDeclineBreakdown}
                />
              </div>
            </div>
          )}

          {/* SLIDE PANEL (Bookings Overlay) */}
          {bookingFilters && (
            <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-end">
              <div className="w-[85%] xl:w-[70%] h-full bg-[#0f0f0f] shadow-2xl transform transition-transform duration-300 translate-x-0 overflow-auto">
                <div className="flex justify-between items-center p-4 border-b border-gray-700">
                  <h2 className="text-white text-lg font-bold">
                    Filtered Bookings
                  </h2>
                  <button
                    onClick={handleBackToDashboard}
                    className="text-white bg-[#FBB033] px-4 py-2 rounded-md"
                  >
                    Close
                  </button>
                </div>

                <FilteredBookings
                  filters={bookingFilters}
                  onBack={handleBackToDashboard}
                />
              </div>
            </div>
          )}
        </div>
      </Layout>
    </div>
  );
};

export default AdminDashboard;
